import React, { useState, useEffect, FC } from "react";
import { useTranslation } from "react-i18next";
import { VatSelector, EditableDropdown, Button } from "@components";
import { safeParseFloat } from "../../utils/parseNumber";
import { formFooter } from "@design-system";

// Interface for form data
interface ITimePriceFormData {
  price: number | string; // Allow string for input flexibility
  vat: number;
  timePeriodInMinutes: number;
}

// Interface for component props
export interface TimePriceFormProps {
  initialPrice?: number;
  initialVat?: number;
  initialTimePeriod?: number;
  onConfirm: (data: {
    price: number;
    vat: number;
    timePeriodInMinutes: number;
  }) => void;
  onCancel: () => void;
}

export const TimePriceForm: FC<TimePriceFormProps> = ({
  initialPrice = 0, // Keep default prop value as 0 for logic
  initialVat = 25,
  initialTimePeriod = 60,
  onConfirm,
  onCancel,
}) => {
  const { t } = useTranslation(["common"]);

  // Single state object for actual form data
  const [formData, setFormData] = useState<ITimePriceFormData>({
    // Set price to '' if initialPrice is 0 or undefined, otherwise use initialPrice
    price: initialPrice > 0 ? initialPrice : "",
    vat: initialVat,
    timePeriodInMinutes: initialTimePeriod,
  });

  // Effect to reset form when initial props change (e.g., modal reopens)
  useEffect(() => {
    // Set price to '' if initialPrice is 0, otherwise use initialPrice
    setFormData({
      price: initialPrice > 0 ? initialPrice : "",
      vat: initialVat,
      timePeriodInMinutes: initialTimePeriod,
    });
  }, [initialPrice, initialVat, initialTimePeriod]);

  // --- Input Handlers ---

  const handlePriceChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    // Allow empty string or valid non-negative numbers
    if (value === "" || /^[0-9]*\.?[0-9]*$/.test(value)) {
      setFormData((prevData) => ({ ...prevData, price: value }));
    }
  };

  const handleVatChange = (value: number) => {
    setFormData((prevData) => ({ ...prevData, vat: value }));
  };

  const handleTimePeriodChange = (value: number) => {
    setFormData((prevData) => ({ ...prevData, timePeriodInMinutes: value }));
  };

  // --- Form Submission ---

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault(); // Prevent default form submission

    // Validate and format data before confirming
    const finalPrice = safeParseFloat(formData.price); // Ensure price is a number
    const finalVat = formData.vat;
    const finalTimePeriod = formData.timePeriodInMinutes;

    // Simple validation example: price must be non-negative
    if (finalPrice < 0) {
      console.error("Price cannot be negative."); // Replace with user-friendly validation feedback
      // Optionally show a validation message to the user here
      return;
    }
    if (finalVat < 0 || finalVat > 100) {
      console.error("VAT must be between 0 and 100.");
      return;
    }

    onConfirm({
      price: finalPrice,
      vat: finalVat,
      timePeriodInMinutes: finalTimePeriod,
    });
  };

  // --- Render Logic ---

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {/* Description Text */}
      <p className="text-sm text-muted">
        {t(
          "common:time_price_order_level_description",
          "Prisen settes for hele ordren, uavhengig av produktene.",
        )}
      </p>

      {/* Price Input */}
      <div>
        <label
          htmlFor="timePrice"
          className="block mb-2 text-sm font-medium text-text"
        >
          {t("common:price_per_time_unit", "Pris per tidsenhet")}
        </label>
        <div className="relative">
          <input
            type="number"
            id="timePrice"
            name="timePrice"
            min="0"
            step="any" // Allow decimals
            className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 pr-20 dark:bg-lgb-on-dark-background dark:border-gray-500 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
            placeholder={t("common:price_per_time_unit", "Price per time unit")}
            value={formData.price} // Controlled component
            onChange={handlePriceChange}
            required // Basic HTML validation
          />
          {formData.vat > 0 && (
            <span className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500">
              {t("common:kr_excl_vat", "kr eks mva")}
            </span>
          )}
        </div>
      </div>

      {/* Time Period Selection */}
      <div>
        <label className="block mb-2 text-sm font-medium text-text">
          {t("common:time_period", "Tidsperiode")}
        </label>
        <EditableDropdown
          value={formData.timePeriodInMinutes}
          onChange={handleTimePeriodChange}
          placeholder={t("common:minutes_placeholder", "Skriv minutter")}
          suffix={t("common:minutes", "minutter")}
          min={1}
          step={1}
          items={[
            {
              key: "1",
              label: t("common:1_minute", "1 minutt"),
              value: "1",
            },
            {
              key: "10",
              label: t("common:10_minutes", "10 minutter"),
              value: "10",
            },
            {
              key: "30",
              label: t("common:30_minutes", "30 minutter"),
              value: "30",
            },
            {
              key: "60",
              label: t("common:60_minutes", "60 minutter"),
              value: "60",
            },
            {
              key: "120",
              label: t("common:120_minutes", "120 minutter"),
              value: "120",
            },
          ]}
        />
      </div>

      {/* VAT Section - Now using the VatSelector component */}
      <VatSelector
        initialValue={formData.vat}
        presetValues={[12, 15, 25]}
        onChange={handleVatChange}
        labelText={t("vat_percentage")}
        descriptionText={t("vat_percentage_description")}
      />

      {/* Form Buttons */}
      <div className={formFooter}>
        <Button type="button" color="gray" onClick={onCancel}>
          {t("cancel")}
        </Button>
        <Button type="submit" color="primary">
          {t("save")}
        </Button>
      </div>
    </form>
  );
};
